import { apiClient } from '@features/shared/api/client'
import type {
  SupportAttachment,
  SupportAttachmentLimits,
  SupportCategoriesResponse,
  SupportRequestPayload,
  SupportRequestResponse,
} from './contracts'

export async function getSupportCategories(): Promise<SupportCategoriesResponse> {
  const { data } = await apiClient.get<SupportCategoriesResponse>('/support/categories')
  return data
}

export async function submitSupportRequest(
  payload: SupportRequestPayload
): Promise<SupportRequestResponse> {
  const { data } = await apiClient.post<SupportRequestResponse>('/support/request', payload)
  return data
}

// A 20 MB PDF on a slow connection takes longer than the API client's 30 s.
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000

export async function getSupportAttachmentLimits(): Promise<SupportAttachmentLimits> {
  const { data } = await apiClient.get<SupportAttachmentLimits>('/support/attachments/limits')
  return data
}

/** Uploads one file; it waits on the server until a request carries it. */
export async function uploadSupportAttachment(
  file: File,
  options: { signal?: AbortSignal; onProgress?: (fraction: number) => void } = {}
): Promise<SupportAttachment> {
  const form = new FormData()
  form.append('file', file, file.name)
  // postForm, not post: the client's JSON Content-Type would make axios send
  // the form as JSON, with no file. postForm sends multipart, and the browser
  // adds the boundary.
  const { data } = await apiClient.postForm<SupportAttachment>('/support/attachments', form, {
    signal: options.signal,
    timeout: UPLOAD_TIMEOUT_MS,
    onUploadProgress: (event) => {
      if (event.total) options.onProgress?.(event.loaded / event.total)
    },
  })
  return data
}

/** Takes an upload off the form before the request is sent. */
export async function removeSupportAttachment(attachmentId: string): Promise<void> {
  await apiClient.delete(`/support/attachments/${encodeURIComponent(attachmentId)}`)
}
